import { Chip, Typography } from '@mui/material'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { pushDrill } from 'app/slice/executive.slice'
import LeaderboardCard from './LeaderboardCard'
import type { LeaderboardRow } from './LeaderboardCard'
import { useTopProcedures } from '../hooks/use-top-procedures'
import { formatCurrency } from 'utils/executive.utils'

// Concentration thresholds — when a single procedure or the top 3 dominate
// the Top N pool, surface a warning so the user notices revenue concentration
// risk without tallying the numbers themselves.
const TOP1_CONCENTRATION_PCT = 40
const TOP3_CONCENTRATION_PCT = 70

function varianceOf(current: number, previous: number | null | undefined): number | null {
  if (previous == null || previous === 0) return null
  return (current - previous) / Math.abs(previous)
}

export default function TopProceduresCard() {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { data, loading, error, refetch } = useTopProcedures()

  const title = t('EXECUTIVE.SALES.TOP_PROCEDURES.TITLE')

  const rows: LeaderboardRow[] =
    data?.items.map((i) => ({
      id: i.procedureId,
      name: i.procedureName,
      revenue: i.revenue,
      count: i.caseCount,
      avgPerItem: i.avgPerCase,
      percentShare: i.percentShare,
      variance: varianceOf(i.revenue, i.previousRevenue),
    })) ?? []

  // Concentration check — % of top-N pool captured by the leader and the top 3.
  // Note: percentShare here is share of the Top N total, not share of clinic-wide
  // revenue (that distinction is called out in the title tooltip).
  const top1Share = rows[0]?.percentShare ?? 0
  const top3Share = rows.slice(0, 3).reduce((a, r) => a + (r.percentShare || 0), 0)
  const concentrated =
    top1Share >= TOP1_CONCENTRATION_PCT || top3Share >= TOP3_CONCENTRATION_PCT

  const subtitle = data
    ? t(
        'EXECUTIVE.SALES.TOP_PROCEDURES.SUBTITLE',
        'Top {{count}} • รวม {{total}}',
        {
          count: rows.length,
          total: formatCurrency(data.totalRevenue),
        }
      )
    : null

  const titleTooltip = t(
    'EXECUTIVE.SALES.TOP_PROCEDURES.TOOLTIP',
    '10 หัตถการที่ทำรายได้สูงสุดในช่วงวันที่เลือก เรียงตามยอดที่ลูกค้าชำระจริง (ไม่นับใบที่ถูกยกเลิก)\nหมายเหตุ: "สัดส่วน" = % ของยอด Top 10 ไม่ใช่ % ของรายได้รวมทั้งคลินิก'
  )

  const notice = concentrated ? (
    <Chip
      icon={<WarningAmberOutlinedIcon />}
      label={
        <Typography variant="caption" sx={{ whiteSpace: 'normal' }}>
          {top1Share >= TOP1_CONCENTRATION_PCT
            ? t(
                'EXECUTIVE.SALES.TOP_PROCEDURES.CONCENTRATION_TOP1',
                'รายได้กระจุก: หัตถการอันดับ 1 = {{pct}}% ของ Top 10',
                { pct: top1Share.toFixed(0) }
              )
            : t(
                'EXECUTIVE.SALES.TOP_PROCEDURES.CONCENTRATION_TOP3',
                'รายได้กระจุก: Top 3 = {{pct}}% ของ Top 10',
                { pct: top3Share.toFixed(0) }
              )}
        </Typography>
      }
      color="warning"
      variant="outlined"
      size="small"
      sx={{ height: 'auto', '& .MuiChip-label': { px: 1, py: 0.25 } }}
    />
  ) : null

  return (
    <LeaderboardCard
      title={title}
      titleTooltip={titleTooltip}
      subtitle={subtitle}
      notice={notice}
      emptyMessage={t(
        'EXECUTIVE.SALES.TOP_PROCEDURES.EMPTY',
        'ไม่พบหัตถการในช่วงเวลาและสาขาที่เลือก'
      )}
      onClick={() => dispatch(pushDrill({ id: 'top-procedures', label: title }))}
      countColumnLabel={t('EXECUTIVE.SALES.COLUMN.CASES')}
      avgColumnLabel={t('EXECUTIVE.SALES.COLUMN.AVG_PER_CASE')}
      countUnit={t('EXECUTIVE.SALES.UNIT.CASE')}
      rows={rows}
      totalRevenue={data?.totalRevenue ?? 0}
      loading={loading}
      error={error}
      onRetry={refetch}
      columnHeaders={{
        rank: t('EXECUTIVE.SALES.COLUMN.RANK'),
        name: t('EXECUTIVE.SALES.COLUMN.NAME'),
        revenue: t('EXECUTIVE.SALES.COLUMN.REVENUE'),
        share: t('EXECUTIVE.SALES.COLUMN.SHARE'),
      }}
    />
  )
}
